import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";

function DepartmentList() {
	const [departments, setDepartments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showBenchModal, setShowBenchModal] = useState(false);
	const [editingDepartment, setEditingDepartment] = useState(null);
	const [editingBench, setEditingBench] = useState(null);
	const [selectedDepartment, setSelectedDepartment] = useState(null);
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		minWeekdayStaff: "",
		minWeekendStaff: "",
	});
	const [benchForm, setBenchForm] = useState({
		name: "",
		weekdayStaffNeeded: 1,
		saturdayStaffNeeded: 1,
		sundayStaffNeeded: 1,
		holidayStaffNeeded: "",
		requirementType: "mandatory",
	});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	useEffect(() => {
		loadDepartments();
	}, []);

	const loadDepartments = async () => {
		try {
			const response = await axios.get(`${API_URL}/departments`);
			setDepartments(response.data);
		} catch (error) {
			setError("Failed to load departments");
		} finally {
			setLoading(false);
		}
	};

	const openModal = (department = null) => {
		if (department) {
			setEditingDepartment(department);
			setFormData({
				name: department.name,
				description: department.description || "",
				minWeekdayStaff:
					department.min_weekday_staff !== null
						? department.min_weekday_staff
						: "",
				minWeekendStaff:
					department.min_weekend_staff !== null
						? department.min_weekend_staff
						: "",
			});
		} else {
			setEditingDepartment(null);
			setFormData({
				name: "",
				description: "",
				minWeekdayStaff: "",
				minWeekendStaff: "",
			});
		}
		setShowModal(true);
		setError("");
	};

	const closeModal = () => {
		setShowModal(false);
		setEditingDepartment(null);
		setError("");
	};

	const openBenchModal = (department, bench = null) => {
		setSelectedDepartment(department);
		if (bench) {
			setEditingBench(bench);
			setBenchForm({
				name: bench.name,
				weekdayStaffNeeded: bench.weekday_staff_needed,
				saturdayStaffNeeded: bench.saturday_staff_needed,
				sundayStaffNeeded: bench.sunday_staff_needed,
				holidayStaffNeeded:
					bench.holiday_staff_needed !== null &&
					bench.holiday_staff_needed !== undefined
						? bench.holiday_staff_needed
						: "",
				requirementType:
					bench.requirement_type === "soft"
						? "optional"
						: "mandatory",
			});
		} else {
			setEditingBench(null);
			setBenchForm({
				name: "",
				weekdayStaffNeeded: 1,
				saturdayStaffNeeded: 1,
				sundayStaffNeeded: 1,
				holidayStaffNeeded: "",
				requirementType: "mandatory",
			});
		}
		setShowBenchModal(true);
		setError("");
	};

	const closeBenchModal = () => {
		setShowBenchModal(false);
		setSelectedDepartment(null);
		setEditingBench(null);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		try {
			if (editingDepartment) {
				await axios.put(
					`${API_URL}/departments/${editingDepartment.id}`,
					formData,
				);
				setSuccess("Department updated successfully");
			} else {
				await axios.post(`${API_URL}/departments`, formData);
				setSuccess("Department created successfully");
			}

			closeModal();
			loadDepartments();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError(
				error.response?.data?.error || "Failed to save department",
			);
		}
	};

	const handleAddBench = async (e) => {
		e.preventDefault();
		setError("");

		try {
			if (editingBench) {
				await axios.put(
					`${API_URL}/departments/${selectedDepartment.id}/benches/${editingBench.id}`,
					benchForm,
				);
				setSuccess("Bench updated successfully");
			} else {
				await axios.post(
					`${API_URL}/departments/${selectedDepartment.id}/benches`,
					benchForm,
				);
				setSuccess("Bench added successfully");
			}
			closeBenchModal();
			loadDepartments();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError(error.response?.data?.error || "Failed to save bench");
		}
	};

	const handleDeleteBench = async (deptId, benchId) => {
		try {
			await axios.delete(
				`${API_URL}/departments/${deptId}/benches/${benchId}`,
			);
			setSuccess("Bench deleted successfully");
			loadDepartments();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to delete bench");
		}
	};

	const handleDelete = async (id) => {
		try {
			await axios.delete(`${API_URL}/departments/${id}`);
			setSuccess("Department deleted successfully");
			loadDepartments();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to delete department");
		}
	};

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='page-header'>
				<h1 className='page-title'>Departments & Benches</h1>
				<button className='btn btn-primary' onClick={() => openModal()}>
					+ Add Department
				</button>
			</div>

			{success && <div className='alert alert-success'>{success}</div>}
			{error && <div className='alert alert-error'>{error}</div>}

			{departments.length === 0 ? (
				<div className='card text-center' style={{ padding: "3rem" }}>
					<p className='text-muted mb-md'>
						No departments found. Create your first department to
						get started.
					</p>
					<button
						className='btn btn-primary'
						onClick={() => openModal()}
					>
						Create Department
					</button>
				</div>
			) : (
				<div style={{ display: "grid", gap: "1rem" }}>
					{departments.map((dept) => (
						<div key={dept.id} className='card'>
							<div className='card-header'>
								<div>
									<h3 style={{ margin: 0 }}>{dept.name}</h3>
									{dept.description && (
										<p
											className='text-sm text-muted'
											style={{ margin: "0.25rem 0 0" }}
										>
											{dept.description}
										</p>
									)}
									{(dept.min_weekday_staff !== null ||
										dept.min_weekend_staff !== null) && (
										<p
											className='text-sm'
											style={{
												margin: "0.25rem 0 0",
												color: "#666",
											}}
										>
											Min Staff: Weekday{" "}
											{dept.min_weekday_staff ?? "auto"} |
											Weekend{" "}
											{dept.min_weekend_staff ?? "auto"}
											{((dept.min_weekday_staff !==
												null &&
												dept.min_weekday_staff < 1) ||
												(dept.min_weekend_staff !==
													null &&
													dept.min_weekend_staff <
														1)) && (
												<span
													style={{
														color: "#e67e22",
														marginLeft: "0.5rem",
													}}
												>
													(pseudo-department pairing)
												</span>
											)}
										</p>
									)}
								</div>
								<div className='flex gap-sm'>
									<button
										className='btn btn-sm btn-accent'
										onClick={() => openBenchModal(dept)}
									>
										+ Add Bench
									</button>
									<button
										className='btn btn-sm btn-secondary'
										onClick={() => openModal(dept)}
									>
										Edit
									</button>
									<button
										className='btn btn-sm btn-danger'
										onClick={() => handleDelete(dept.id)}
									>
										Delete
									</button>
								</div>
							</div>

							{dept.benches && dept.benches.length > 0 ? (
								<div className='table-container'>
									<table className='table'>
										<thead>
											<tr>
												<th>Bench Name</th>
												<th>Weekday Staff</th>
												<th>Saturday Staff</th>
												<th>Sunday Staff</th>
												<th>Holiday Staff</th>
												<th>Bench Type</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{dept.benches.map((bench) => {
												return (
													<tr key={bench.id}>
														<td className='font-medium'>
															{bench.name}
														</td>
														<td>
															{
																bench.weekday_staff_needed
															}
														</td>
														<td>
															{
																bench.saturday_staff_needed
															}
														</td>
														<td>
															{
																bench.sunday_staff_needed
															}
														</td>
														<td>
															{bench.holiday_staff_needed !==
																null &&
															bench.holiday_staff_needed !==
																undefined ? (
																bench.holiday_staff_needed
															) : (
																<span
																	style={{
																		color: "var(--gray-400)",
																		fontSize:
																			"0.8rem",
																	}}
																>
																	—
																</span>
															)}
														</td>
														<td>
															<span
																className={`badge ${
																	bench.requirement_type ===
																	"soft"
																		? "badge-secondary"
																		: "badge-primary"
																}`}
															>
																{bench.requirement_type ===
																"soft"
																	? "Optional"
																	: "Mandatory"}
															</span>
														</td>
														<td>
															<div className='flex gap-sm'>
																<button
																	className='btn btn-sm btn-secondary'
																	onClick={() =>
																		openBenchModal(
																			dept,
																			bench,
																		)
																	}
																>
																	Edit
																</button>
																<button
																	className='btn btn-sm btn-danger'
																	onClick={() =>
																		handleDeleteBench(
																			dept.id,
																			bench.id,
																		)
																	}
																>
																	Delete
																</button>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							) : (
								<p
									className='text-muted text-sm'
									style={{ padding: "1rem 0" }}
								>
									No benches configured. Add benches to define
									staffing needs for this department.
								</p>
							)}
						</div>
					))}
				</div>
			)}

			{/* Department Modal */}
			{showModal && (
				<div className='modal-overlay' onClick={closeModal}>
					<div className='modal' onClick={(e) => e.stopPropagation()}>
						<div className='modal-header'>
							<h3>
								{editingDepartment
									? "Edit Department"
									: "Add Department"}
							</h3>
							<button
								className='modal-close'
								onClick={closeModal}
							>
								&times;
							</button>
						</div>
						<form onSubmit={handleSubmit}>
							<div className='modal-body'>
								{error && (
									<div className='alert alert-error'>
										{error}
									</div>
								)}

								<div className='form-group'>
									<label className='form-label'>
										Department Name *
									</label>
									<input
										type='text'
										className='form-input'
										value={formData.name}
										onChange={(e) =>
											setFormData({
												...formData,
												name: e.target.value,
											})
										}
										placeholder='e.g., Hematology, Chemistry, Blood Bank'
										required
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Description
									</label>
									<textarea
										className='form-textarea'
										value={formData.description}
										onChange={(e) =>
											setFormData({
												...formData,
												description: e.target.value,
											})
										}
										placeholder='Optional description of this department'
										rows={3}
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Minimum Staff - Weekday
									</label>
									<input
										type='number'
										step='0.5'
										min='0'
										className='form-input'
										value={formData.minWeekdayStaff}
										onChange={(e) =>
											setFormData({
												...formData,
												minWeekdayStaff: e.target.value,
											})
										}
										placeholder='e.g., 2.5 (leave empty to use bench totals)'
									/>
									<small className='text-muted'>
										Minimum dedicated staff needed. Values
										below 1 indicate the department relies
										on pseudo-department pairing (CM/UM).
									</small>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Minimum Staff - Weekend/Holiday
									</label>
									<input
										type='number'
										step='0.5'
										min='0'
										className='form-input'
										value={formData.minWeekendStaff}
										onChange={(e) =>
											setFormData({
												...formData,
												minWeekendStaff: e.target.value,
											})
										}
										placeholder='e.g., 1.5 (leave empty to use bench totals)'
									/>
									<small className='text-muted'>
										Minimum dedicated staff for
										weekends/holidays. Values below 1 rely
										on pseudo-department pairing (CM/UM).
									</small>
								</div>
							</div>
							<div className='modal-footer'>
								<button
									type='button'
									className='btn btn-secondary'
									onClick={closeModal}
								>
									Cancel
								</button>
								<button
									type='submit'
									className='btn btn-primary'
								>
									{editingDepartment
										? "Update Department"
										: "Create Department"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* Bench Modal */}
			{showBenchModal && (
				<div className='modal-overlay' onClick={closeBenchModal}>
					<div className='modal' onClick={(e) => e.stopPropagation()}>
						<div className='modal-header'>
							<h3>
								{editingBench
									? `Edit Bench: ${editingBench.name}`
									: `Add Bench to ${selectedDepartment?.name}`}
							</h3>
							<button
								className='modal-close'
								onClick={closeBenchModal}
							>
								&times;
							</button>
						</div>
						<form onSubmit={handleAddBench}>
							<div className='modal-body'>
								{error && (
									<div className='alert alert-error'>
										{error}
									</div>
								)}

								<div className='form-group'>
									<label className='form-label'>
										Bench Name *
									</label>
									<input
										type='text'
										className='form-input'
										value={benchForm.name}
										onChange={(e) =>
											setBenchForm({
												...benchForm,
												name: e.target.value,
											})
										}
										placeholder='e.g., Bench 1, Main Bench, CBC Analyzer'
										required
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Staff Needed (Weekdays)
									</label>
									<input
										type='number'
										min='0'
										className='form-input'
										value={benchForm.weekdayStaffNeeded}
										onChange={(e) =>
											setBenchForm({
												...benchForm,
												weekdayStaffNeeded:
													parseInt(e.target.value) ||
													0,
											})
										}
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Staff Needed (Saturday)
									</label>
									<input
										type='number'
										min='0'
										className='form-input'
										value={benchForm.saturdayStaffNeeded}
										onChange={(e) =>
											setBenchForm({
												...benchForm,
												saturdayStaffNeeded:
													parseInt(e.target.value) ||
													0,
											})
										}
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Staff Needed (Sunday)
									</label>
									<input
										type='number'
										min='0'
										className='form-input'
										value={benchForm.sundayStaffNeeded}
										onChange={(e) =>
											setBenchForm({
												...benchForm,
												sundayStaffNeeded:
													parseInt(e.target.value) ||
													0,
											})
										}
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Staff Needed (Holidays)
									</label>
									<input
										type='number'
										min='0'
										className='form-input'
										value={benchForm.holidayStaffNeeded}
										placeholder='Leave blank to use Sunday staffing level'
										onChange={(e) =>
											setBenchForm({
												...benchForm,
												holidayStaffNeeded:
													e.target.value,
											})
										}
									/>
									<small className='text-muted'>
										Set the number of staff needed on
										federal holidays. Leave blank (or 0) if
										this bench is closed on holidays.
									</small>
								</div>

								<div className='form-group'>
									<p className='text-sm text-muted'>
										Dual-bench day configuration has been
										removed. Scheduler now uses CM
										(SMU+Micro) and UM (UA+Micro)
										pseudo-department pairing automatically.
									</p>
								</div>
							</div>
							<div className='modal-footer'>
								<button
									type='button'
									className='btn btn-secondary'
									onClick={closeBenchModal}
								>
									Cancel
								</button>
								<button
									type='submit'
									className='btn btn-primary'
								>
									{editingBench
										? "Update Bench"
										: "Add Bench"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default DepartmentList;
